/**
 * =============================================================================
 * Página: /afiliado (Dashboard) — Redesign Premium
 * Versão: 2.0.0
 *
 * Descrição:
 *   Home premium mobile-first do painel do afiliado:
 *     1. Hero gradiente com saldo disponível em display size + CTA "Sacar".
 *     2. Faixa de quick actions (Copiar / WhatsApp / QR) usando o 1º link.
 *     3. Anel de progresso do mês (conversão %) + micro-stats.
 *     4. Funil compacto (cliques → leads → conversões).
 *     5. Detalhamento por programa.
 *     6. Últimas indicações (lista limpa).
 *     7. FAB "Compartilhar" (mobile) ancorado acima da bottom-nav.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Share2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { useAffiliateDashboardKpis } from '@/hooks/useAffiliateDashboardKpis';
import { useAffiliateMonthlyProgress } from '@/hooks/useAffiliateMonthlyProgress';
import { useAffiliateHomeOverview } from '@/hooks/useAffiliateHomeOverview';
import { useAffiliateReferrals } from '@/hooks/useAffiliateReferrals';
import { useAffiliateMyCampaigns } from '@/hooks/useAffiliateMyCampaigns';
import { useSelectedAffiliateCampaign } from '@/hooks/useSelectedAffiliateCampaign';
import { useAffiliateLinkActions } from '@/hooks/useAffiliateLinkActions';
import { useAffiliateMonthlyEvolution } from '@/hooks/useAffiliateMonthlyEvolution';
import { TaskTeamChart } from '@/components/tasks/dashboard/TaskTeamChart';
import { HeroBalanceCard } from '@/components/affiliate/home/HeroBalanceCard';
import { QuickActionsRow } from '@/components/affiliate/home/QuickActionsRow';
import { MonthlyGoalRing } from '@/components/affiliate/home/MonthlyGoalRing';
import { FunnelStrip } from '@/components/affiliate/home/FunnelStrip';
import { CampaignsCarousel } from '@/components/affiliate/home/CampaignsCarousel';

import {
  AffiliateKpiReferralsModal,
  type AffiliateKpiMetric,
} from '@/components/affiliate/home/AffiliateKpiReferralsModal';

import { AffiliateProgramsBreakdownCard } from '@/components/affiliate/AffiliateProgramsBreakdownCard';
import { KpiInfoTooltip } from '@/components/affiliate/shared/KpiInfoTooltip';

import {
  PeriodFilterBar,
  DEFAULT_PERIOD,
  resolvePeriod,
  type PeriodFilterValue,
} from '@/components/affiliate/PeriodFilterBar';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/** Saudação adequada ao horário local. */
function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

const REFERRAL_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  qualified: 'Qualificado',
  converted: 'Convertido',
  lost: 'Perdido',
  cancelled: 'Cancelado',
};

const REFERRAL_STATUS_TONE: Record<string, string> = {
  pending: 'bg-[hsl(var(--aff-surface-2))] text-[hsl(var(--aff-muted-foreground))]',
  qualified: 'bg-[hsl(var(--aff-accent-2)/0.15)] text-[hsl(var(--aff-accent-2))]',
  converted: 'bg-[hsl(var(--aff-success)/0.15)] text-[hsl(var(--aff-success))]',
  lost: 'bg-[hsl(var(--aff-danger)/0.12)] text-[hsl(var(--aff-danger))]',
  cancelled: 'bg-[hsl(var(--aff-danger)/0.12)] text-[hsl(var(--aff-danger))]',
};

/** Cor do "dot" indicador de status (apenas a bolinha colorida). */
const REFERRAL_STATUS_DOT: Record<string, string> = {
  pending: 'bg-[hsl(var(--aff-muted-foreground))]',
  qualified: 'bg-[hsl(var(--aff-accent-2))]',
  converted: 'bg-[hsl(var(--aff-success))]',
  lost: 'bg-[hsl(var(--aff-danger))]',
  cancelled: 'bg-[hsl(var(--aff-danger))]',
};

/** Cor do texto do status (acompanha o dot). */
const REFERRAL_STATUS_TEXT: Record<string, string> = {
  pending: 'text-[hsl(var(--aff-muted-foreground))]',
  qualified: 'text-[hsl(var(--aff-accent-2))]',
  converted: 'text-[hsl(var(--aff-success))]',
  lost: 'text-[hsl(var(--aff-danger))]',
  cancelled: 'text-[hsl(var(--aff-danger))]',
};

/** Cor determinística para avatar de inicial do lead. */
function avatarColor(seed: string): string {
  const hues = [252, 280, 215, 165, 38, 0];
  let acc = 0;
  for (let i = 0; i < seed.length; i++) acc = (acc + seed.charCodeAt(i)) % 360;
  const hue = hues[acc % hues.length];
  return `hsl(${hue} 70% 92%)`;
}
function avatarTextColor(seed: string): string {
  const hues = [252, 280, 215, 165, 38, 0];
  let acc = 0;
  for (let i = 0; i < seed.length; i++) acc = (acc + seed.charCodeAt(i)) % 360;
  const hue = hues[acc % hues.length];
  return `hsl(${hue} 70% 35%)`;
}

export default function AffiliateDashboard() {
  const { affiliate } = useAffiliateAuth();

  // -----------------------------------------------------------------
  // Filtro de período (Hoje · Ontem · Este mês · Personalizado)
  // -----------------------------------------------------------------
  const [period, setPeriod] = useState<PeriodFilterValue>(DEFAULT_PERIOD);
  const { start, end } = useMemo(() => resolvePeriod(period), [period]);
  const periodParams = { startDate: start, endDate: end };

  // Drill-down: KPI clicado abre o modal com a listagem de indicações.
  const [drilldownMetric, setDrilldownMetric] = useState<AffiliateKpiMetric | null>(null);



  // Label resumido do período para o cabeçalho do card de progresso
  const periodLabel = useMemo(() => {
    if (period.preset === 'today') return 'Hoje';
    if (period.preset === 'yesterday') return 'Ontem';
    if (period.preset === 'custom' && period.range?.from && period.range?.to) {
      const f = format(period.range.from, 'dd/MM', { locale: ptBR });
      const t = format(period.range.to, 'dd/MM', { locale: ptBR });
      return `${f} – ${t}`;
    }
    // mês
    const m = format(period.month ?? new Date(), 'MMMM', { locale: ptBR });
    return m.charAt(0).toUpperCase() + m.slice(1);
  }, [period]);

  const progressTitle =
    period.preset === 'month' ? 'Progresso do mês' : 'Progresso do período';

  // -----------------------------------------------------------------
  // Hooks de dados — todos respeitam o período
  // -----------------------------------------------------------------
  const overview = useAffiliateHomeOverview(periodParams);
  const { data: kpis, isLoading: kpisLoading } = useAffiliateDashboardKpis(periodParams);
  const { data: monthly } = useAffiliateMonthlyProgress(periodParams);
  // Lista de "últimas indicações" continua mostrando as 5 mais recentes (sem filtro).
  const { data: referrals, isLoading: refLoading } = useAffiliateReferrals();
  const { data: campaigns, isLoading: campaignsLoading } = useAffiliateMyCampaigns();
  const { shareWhatsapp, isSharingWhatsapp } = useAffiliateLinkActions();
  // Evolução mensal (6 meses) — Leads vs Ganhos. Período fixo, igual ao
  // gráfico do dashboard de Tarefas (não respeita o PeriodFilterBar).
  const { data: monthlyEvolution, isLoading: monthlyEvolutionLoading } =
    useAffiliateMonthlyEvolution();

  // -----------------------------------------------------------------
  // Multi-campanha: seleção persistida em localStorage por afiliado.
  // Compartilhada entre QuickActionsRow (3 ações) e FAB de Share.
  // -----------------------------------------------------------------
  const { selectedCampaign, setSelectedCampaignId } =
    useSelectedAffiliateCampaign(campaigns, affiliate?.id);

  const recent = (referrals ?? []).slice(0, 5);
  const firstName = affiliate?.full_name?.split(' ')[0] ?? 'Afiliado';

  const handleFabShare = async () => {
    if (!selectedCampaign) {
      toast.info('Nenhuma campanha disponível para compartilhar ainda.');
      return;
    }
    try {
      const res = await shareWhatsapp({ linkId: selectedCampaign.link_id });
      window.open(res.whatsapp_url, '_blank', 'noopener,noreferrer');
      toast.success(`Compartilhando "${selectedCampaign.campaign_name}" no WhatsApp`);
    } catch {
      toast.error('Não foi possível abrir o WhatsApp.');
    }
  };

  // Data atual em português para o subtítulo editorial do header desktop
  const todayLabel = useMemo(() => {
    const s = format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR });
    return s.charAt(0).toUpperCase() + s.slice(1);
  }, []);

  return (
    <>
      <div className="mx-auto max-w-5xl px-4 pt-4 md:px-8 md:pt-8 space-y-8">
        {/* Saudação + ações + filtro de período (desktop) */}
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="hidden md:block">
            <p className="text-[12px] font-medium uppercase tracking-[0.1em] text-[hsl(var(--aff-muted-foreground))]">
              {greeting()}
            </p>
            <h1 className="mt-1 text-[28px] font-semibold tracking-tight text-[hsl(var(--aff-foreground))]">
              {firstName}
            </h1>
            <p className="mt-1 text-[12.5px] text-[hsl(var(--aff-muted-foreground))]">
              {todayLabel}
            </p>
          </div>

          {/* Filtro de período (botão "Nova Indicação" agora vive no header global) */}
          <PeriodFilterBar
            value={period}
            onChange={setPeriod}
            className="md:flex-1 md:max-w-[640px] md:justify-end"
          />
        </div>

        {/* Linha A: Hero (1/2) + Progresso (1/2) — inalterado */}
        <div className="grid gap-6 md:grid-cols-2 md:items-stretch">
          <HeroBalanceCard
            isLoading={kpisLoading}
            availableBalance={kpis?.availableBalance ?? 0}
            inTransit={kpis?.inTransit ?? 0}
            totalEarned={kpis?.totalEarned ?? 0}
            greeting={greeting()}
            firstName={firstName}
          />

          {/* Mobile: QuickActions aparece entre Hero e Progresso (ordem visual) */}
          <div className="md:hidden">
            <QuickActionsRow
              campaigns={campaigns}
              selectedCampaign={selectedCampaign}
              onSelectCampaign={setSelectedCampaignId}
            />
          </div>

          <MonthlyGoalRing
            params={periodParams}
            periodLabel={periodLabel}
            title={progressTitle}
            onMetricClick={setDrilldownMetric}
          />

        </div>

        {/* Linha B (desktop): coluna esquerda = QuickActions + Evolução 6 meses
            empilhados verticalmente; coluna direita = Funil 3D ocupando altura total.
            Mobile: QuickActions já apareceu na Linha A; aqui mostramos somente o
            Funil. A Evolução vai num bloco separado abaixo no mobile. */}
        <div className="grid gap-6 md:grid-cols-2 md:items-stretch">
          {/* Coluna esquerda — só desktop */}
          <div className="hidden md:flex md:flex-col md:gap-6">
            <QuickActionsRow
              campaigns={campaigns}
              selectedCampaign={selectedCampaign}
              onSelectCampaign={setSelectedCampaignId}
            />
            {/* Evolução mensal (6m) — Indicações vs Ganhos */}
            <div className="space-y-2">
              <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aff-muted-foreground))]">
                EVOLUÇÃO (6 MESES)
                <KpiInfoTooltip
                  label="Evolução 6 meses"
                  hint="Indicações e Ganhos dos últimos 6 meses (período fixo, não respeita o filtro acima). Indicações conta pela data de criação do lead; Ganhos conta pela data em que a conversão ocorreu."
                />
              </h3>
              {monthlyEvolutionLoading ? (
                <Skeleton className="h-[220px] w-full rounded-xl" />
              ) : (
                // Aplica o mesmo hover dos demais KPIs (elevação + sombra)
                // diretamente no <Card> interno do TaskTeamChart.
                <div className="contents [&>div]:transition-all [&>div]:duration-200 [&>div]:hover:shadow-md [&>div]:hover:-translate-y-0.5">
                  <TaskTeamChart
                    data={(monthlyEvolution ?? []).slice(-6) as unknown as Array<{ month_label: string; [key: string]: unknown }>}
                    title="​"
                    series={[
                      {
                        dataKey: 'referrals_count',
                        name: 'Indicações',
                        color: 'hsl(var(--aff-accent-2))',
                      },
                      {
                        dataKey: 'conversions_count',
                        name: 'Ganhos',
                        color: 'hsl(var(--aff-success))',
                      },
                    ]}
                    showYAxis
                  />
                </div>
              )}
            </div>
          </div>

          {/* Coluna direita — Funil ocupa altura total da linha */}
          <FunnelStrip
            isLoading={kpisLoading}
            clicks={kpis?.totalClicks ?? 0}
            leads={kpis?.totalReferrals ?? 0}
            conversions={kpis?.totalConversions ?? 0}
            conversionRate={kpis?.conversionRate ?? 0}
            onMetricClick={setDrilldownMetric}
          />
        </div>

        {/* Carrossel de campanhas liberadas (sem KPIs, para economizar espaço) */}
        <CampaignsCarousel campaigns={campaigns} isLoading={campaignsLoading} />



        {/* Modal de drill-down dos KPIs (auditoria das indicações) */}
        <AffiliateKpiReferralsModal
          metric={drilldownMetric}
          onOpenChange={(open) => {
            if (!open) setDrilldownMetric(null);
          }}
          periodParams={periodParams}
          periodLabel={periodLabel}
        />


        {/* 5) Detalhamento por programa (recebe a overview já filtrada) */}
        <AffiliateProgramsBreakdownCard
          programs={overview.data?.programs}
          isLoading={overview.isLoading}
        />

        {/* Mobile-only: Evolução mensal aparece após o detalhamento por programa
            (no desktop ela já está empilhada com o QuickActions na Linha B). */}
        <div className="md:hidden space-y-2">
          <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aff-muted-foreground))]">
            EVOLUÇÃO (6 MESES)
            <KpiInfoTooltip
              label="Evolução 6 meses"
              hint="Indicações e Ganhos dos últimos 6 meses (período fixo, não respeita o filtro acima). Indicações conta pela data de criação do lead; Ganhos conta pela data em que a conversão ocorreu."
            />
          </h3>
          {monthlyEvolutionLoading ? (
            <Skeleton className="h-[220px] w-full rounded-xl" />
          ) : (
            <TaskTeamChart
              data={(monthlyEvolution ?? []).slice(-6) as unknown as Array<{ month_label: string; [key: string]: unknown }>}
              title="​"
              series={[
                {
                  dataKey: 'referrals_count',
                  name: 'Indicações',
                  color: 'hsl(var(--aff-accent-2))',
                },
                {
                  dataKey: 'conversions_count',
                  name: 'Ganhos',
                  color: 'hsl(var(--aff-success))',
                },
              ]}
              showYAxis
            />
          )}
        </div>

        {/* 6) Últimas indicações */}
        <section aria-label="Últimas indicações" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aff-muted-foreground))]">
              Últimas indicações
            </h2>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-8 gap-1 text-[12px] text-[hsl(var(--aff-primary))] hover:bg-[hsl(var(--aff-primary-soft))] hover:text-[hsl(var(--aff-primary))]"
            >
              <Link to="/afiliado/indicacoes">
                Ver todas <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>

          <div className="aff-card overflow-hidden">
            {refLoading ? (
              <div className="space-y-3 p-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : recent.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm font-semibold text-[hsl(var(--aff-foreground))]">
                  Nenhuma indicação ainda
                </p>
                <p className="mt-1 text-[12px] text-[hsl(var(--aff-muted-foreground))]">
                  Compartilhe seu link para começar a captar leads.
                </p>
                <Button
                  asChild
                  size="sm"
                  className="mt-4 rounded-full bg-[hsl(var(--aff-primary))] text-[hsl(var(--aff-primary-foreground))] hover:bg-[hsl(var(--aff-primary)/0.9)]"
                >
                  <Link to="/afiliado/links">Ver minhas campanhas</Link>
                </Button>
              </div>
            ) : (
              <ul className="divide-y divide-[hsl(var(--aff-border))]/70">
                {recent.map((r) => {
                  const name =
                    r.lead_name || r.lead_email || r.lead_phone || 'Indicação';
                  const initial = (name?.[0] ?? 'L').toUpperCase();
                  // Formatação de data com hora/minuto para layout em colunas (desktop)
                  const dateFull = new Date(r.created_at).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  // Data simples (sem hora) para o subtítulo do mobile
                  const dateShort = new Date(r.created_at).toLocaleDateString('pt-BR');
                  return (
                    <li
                      key={r.id}
                      className="flex items-center gap-3 px-4 py-3 md:grid md:grid-cols-[auto_1.4fr_1.2fr_1fr_auto] md:gap-4"
                    >
                      <span
                        aria-hidden
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[12.5px] font-semibold shrink-0"
                        style={{
                          background: avatarColor(name),
                          color: avatarTextColor(name),
                        }}
                      >
                        {initial}
                      </span>

                      {/* Coluna Lead — no mobile mantém subtítulo "campanha · data"; no desktop só o nome */}
                      <div className="min-w-0 flex-1 md:flex-none">
                        <p className="truncate text-[13.5px] font-semibold text-[hsl(var(--aff-foreground))]">
                          {name}
                        </p>
                        <p className="truncate text-[11.5px] text-[hsl(var(--aff-muted-foreground))] md:hidden">
                          {r.campaign?.name || 'Sem campanha'}
                          {' · '}
                          {dateShort}
                        </p>
                      </div>

                      {/* Coluna Campanha (apenas desktop) */}
                      <div className="hidden md:block min-w-0">
                        <p className="truncate text-[12.5px] text-[hsl(var(--aff-muted-foreground))]">
                          {r.campaign?.name || 'Sem campanha'}
                        </p>
                      </div>

                      {/* Coluna Data com hora/minuto (apenas desktop) */}
                      <div className="hidden md:block min-w-0">
                        <p className="truncate text-[12.5px] text-[hsl(var(--aff-muted-foreground))] tabular-nums">
                          {dateFull}
                        </p>
                      </div>
                      {/* Status: apenas dot colorido + texto preto (sem fundo) */}
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium shrink-0 text-[hsl(var(--aff-foreground))]">
                        <span
                          aria-hidden
                          className={cn(
                            'h-2 w-2 rounded-full',
                            REFERRAL_STATUS_DOT[r.status] ??
                              REFERRAL_STATUS_DOT.pending,
                          )}
                        />
                        {REFERRAL_STATUS_LABEL[r.status] ?? r.status}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* FAB Compartilhar — só mobile, ancorado acima da bottom-nav */}
      <button
        type="button"
        onClick={handleFabShare}
        disabled={isSharingWhatsapp}
        aria-label="Compartilhar meu link no WhatsApp"
        className={cn(
          'aff-fab fixed right-4 z-30 md:hidden',
          'h-14 w-14 rounded-full flex items-center justify-center',
          'transition-transform active:scale-95 disabled:opacity-70',
        )}
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
      >
        <Share2 className="h-6 w-6" strokeWidth={2.2} />
      </button>

    </>
  );
}
